import type { Match } from '@uefadigital/panenka';
import { PkCollapse } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CountryToHead from './CountryToHead';
import HeadToCountry from './HeadToCountry';
import Matches from './Matches';
import MatchResults from './MatchResults';
import SectionTitle from './SectionTitle';
import StatsOverview from './StatsOverview';
import TeamsOverview from './TeamsOverview';
import { getConnectedCompetitions, getOverallStatistics, getTeamId, splitH2HMatches } from './utils';

import { useGetHeadToHeadAdvancedQuery, useGetHeadToHeadQuery, useGetMatchHeadToHeadQuery } from 'api/fspApi';
import { translateEntity } from 'utils/translateEntity';

export interface HeadToHeadData {
  matchData: Match;
  competitionId: string;
  isDesktop: boolean;
  isResultsExpanded?: boolean;
}

export interface MatchesInfo {
  title: string;
  value: string;
}

export const HeadToHead = ({ matchData, competitionId, isDesktop, isResultsExpanded = false }: HeadToHeadData) => {
  const BadgeSize = isDesktop ? 64 : 56;
  const titleStyle =
    'pk-font-size--xl pk-font-size-md--xl2 pk-font-weight--bolder pk-line-height--sd pk-font-style--normal pr-font-size--14';
  const clubNameStyle = 'pk-font-size--m pk-font-size-md--xl pk-text--text-01 pk-font-weight--bold pr-font-size--18';
  const scoreNumberStyle = 'pk-font-size--xl pk-font-size-md--xl2 pk-text--text-01 pk-font-weight--bold';
  const scoreTitleStyle = 'pk-font-size--m pk-font-szie-md--l pk-text--text-02';

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(isResultsExpanded);

  const connectedCompetition = useMemo(() => getConnectedCompetitions(competitionId), [competitionId]);
  const isNationalLeague = matchData.competition.teamCategory === 'NATIONAL';

  const {
    data: headToHead,
    error: headToHeadError,
    isLoading: headToHeadLoading,
  } = useGetHeadToHeadQuery({
    id: connectedCompetition,
    teamId: [getTeamId(matchData?.homeTeam), getTeamId(matchData?.awayTeam)],
  });

  const { data: headToHeadAdvanced, isSuccess: headToHeadAdvancedSucceeded } = useGetHeadToHeadAdvancedQuery({
    id: connectedCompetition,
    teamId: [getTeamId(matchData?.homeTeam), getTeamId(matchData?.awayTeam)],
  });

  const toDate = matchData?.kickOffTime?.date ?? new Date().toISOString().slice(0, 10);

  const {
    data: H2HMatches = [],
    error: H2HMatchesError,
    isLoading: H2HMatchesLoading,
  } = useGetMatchHeadToHeadQuery({
    offset: 0,
    limit: 100,
    opponentTeamIds: [getTeamId(matchData?.homeTeam), getTeamId(matchData?.awayTeam)],
    toDate,
    competitionId: connectedCompetition,
  });

  if (!!H2HMatchesError || !!headToHeadError) {
    return <div />;
  }

  if (headToHeadLoading || H2HMatchesLoading) {
    return <div />;
  }

  const { firstHalf, secondHalf } = splitH2HMatches(H2HMatches);
  const { homeWin, draw, awayWin } = getOverallStatistics(headToHead, matchData);
  const numberOfPreviousMatches = Number(homeWin.value) + Number(draw.value) + Number(awayWin.value);
  const havePreviousMatches = numberOfPreviousMatches > 0;

  return (
    <div className="pk-p--m pk-p-md--xl">
      <SectionTitle title={titleStyle} />
      <div>
        <TeamsOverview
          homeTeam={{
            isTeamAvailable: !!matchData?.homeTeam,
            isNationalTeam: false,
            internationalName: matchData?.homeTeam?.internationalName ?? '',
            countryCode: matchData?.homeTeam?.countryCode,
            displayName: translateEntity(matchData?.homeTeam)?.displayName,
            logoUrl: matchData?.homeTeam?.logoUrl,
          }}
          awayTeam={{
            isTeamAvailable: !!matchData?.awayTeam,
            isNationalTeam: false,
            internationalName: matchData?.awayTeam?.internationalName ?? '',
            countryCode: matchData?.awayTeam?.countryCode,
            displayName: translateEntity(matchData?.awayTeam)?.displayName,
            logoUrl: matchData?.awayTeam?.logoUrl,
          }}
          isDesktop={isDesktop}
          badgeSize={BadgeSize}
          clubName={clubNameStyle}
        />
        <StatsOverview
          homeWin={homeWin}
          draw={draw}
          awayWin={awayWin}
          isDesktop={isDesktop}
          scoreNumber={scoreNumberStyle}
          scoreTitle={scoreTitleStyle}
          havePreviousMatches={havePreviousMatches}
        />
      </div>
      <div className="pk-mt--xs pk-mt-md--m pr-display--none">
        {isDesktop && havePreviousMatches && H2HMatches.length ? (
          <div className="pk-w--100 pk-d--flex">
            <PkCollapse
              className="pk-ml--auto"
              expandedLabel={t('see_matches')}
              collapsedLabel={t('see_matches')}
              expand={isExpanded}
              onPkChange={() => setIsExpanded(!isExpanded)}
            />
          </div>
        ) : (
          <div />
        )}
      </div>
      {isExpanded ? (
        <div>
          {isDesktop ? (
            <Matches title={titleStyle} firstHalf={firstHalf} secondHalf={secondHalf} />
          ) : (
            <div className="pk-px--s pk-px-md--xl2">
              <MatchResults H2HMatchesToDisplay={H2HMatches} />
            </div>
          )}
        </div>
      ) : (
        ''
      )}
      <div
        className={clsx({
          'pk-mt--m pk-mt-md--l': havePreviousMatches,
        })}
      >
        {!isDesktop && havePreviousMatches && H2HMatches.length ? (
          <div className="pk-w--100 pk-d--flex">
            <PkCollapse
              className="pk-ml--auto"
              expandedLabel={t('see_matches')}
              collapsedLabel={t('see_matches')}
              expand={isExpanded}
              onPkChange={() => setIsExpanded(!isExpanded)}
            />
          </div>
        ) : (
          <div />
        )}
      </div>
      {!isNationalLeague && headToHeadAdvancedSucceeded && (
        <HeadToCountry
          isDesktop={isDesktop}
          isResultsExpanded={isResultsExpanded}
          matchData={matchData}
          BadgeSize={BadgeSize}
          ClubName={clubNameStyle}
          ScoreNumber={scoreNumberStyle}
          scoreTitle={scoreTitleStyle}
          headToHeadAdvanced={headToHeadAdvanced}
          Title={titleStyle}
        />
      )}
      {!isNationalLeague && headToHeadAdvancedSucceeded && (
        <CountryToHead
          isDesktop={isDesktop}
          isResultsExpanded={isResultsExpanded}
          matchData={matchData}
          BadgeSize={BadgeSize}
          ClubName={clubNameStyle}
          ScoreNumber={scoreNumberStyle}
          scoreTitle={scoreTitleStyle}
          headToHeadAdvanced={headToHeadAdvanced}
          Title={titleStyle}
        />
      )}
    </div>
  );
};
